import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { X } from "lucide-react";
import { trackEvent } from "@/lib/tracking";
import { withAttribution } from "@/lib/outbound";
import { packs, PACKS_URL, PACK_CTA_LABEL, packCtaAriaLabel } from "@/data/packs";
import { MAP_EMBED_URL } from "@/lib/site";
import { facilitySrcSet } from "@/lib/responsiveImage";
import { HOME_HERO_BLUR } from "@/data/heroBlur";
import ColorKey from "@/components/levels/ColorKey";
import { OPEN_PLAY_SUMMARY } from "@/data/skillGuide";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const veltoLogoBeige = "/Velto_01_Combo_Beige.png";

// Whether the booking-app nudge has already been scheduled during THIS page load.
// Module scope, so it survives client-side SPA route changes (returning to the
// homepage does not re-pop the box) but resets on a full page reload (a hard
// refresh shows it again). Dismissal is in-memory only — intentionally not
// persisted — so a refresh always brings the box back.
let appBoxScheduled = false;

/**
 * Booking-CTA click, same event name and param shape the /book page and the
 * landing pages already use, so everything lands in one GA4 event.
 *
 * `location` names the placement, because the homepage has nine of these and an
 * undifferentiated count would only tell you that the homepage converts, not
 * which button did it. `destination` is the undecorated URL — withAttribution()
 * appends per-visit ad params, which would fragment the report.
 *
 * Deliberately just a handler on the existing anchor: no preventDefault, no
 * href rewrite, no async work. Every one of these links is target="_blank", so
 * the page survives the click and the beacon is never cut off mid-flight.
 */
const trackBooking = (location: string, destination: string) => () =>
  trackEvent("book_court_click", { page: "/", location, destination });

/**
 * SMS opt-in wording, defined once: the signup box renders it, and each opt-in
 * stores it verbatim in sms_consents.consent_text, so the saved record is
 * exactly what the person agreed to. Carriers require the disclosure
 * near-verbatim (brand name, registered entity, "not a condition of purchase",
 * frequency, rates, STOP and HELP) — don't cut words out of it.
 */
const SMS_OPT_IN_LABEL = "Text me about open play, events and club news";
const SMS_DISCLOSURE =
  "By checking this box, you agree to receive recurring automated text messages about open play, events and club news from Velto Pickleball Club (O-O-2 PICKLEBALL USA LLC) at the mobile number provided. Consent is not a condition of any purchase. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for help.";
const SMS_CONSENT_TEXT = `${SMS_OPT_IN_LABEL}. ${SMS_DISCLOSURE} See our Terms of Use and Privacy Policy.`;

/**
 * A US number in any common format → E.164 ("+12125551234"), the form AWS
 * texts to; null if it isn't a valid US number. Same rule as the sms_consents
 * insert policy, so a number that passes here is never refused by the database.
 */
const toUsE164 = (raw: string): string | null => {
  const digits = raw.replace(/\D/g, "");
  const national = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(national) ? `+1${national}` : null;
};

const Index = () => {
  const [loading, setLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isMobile = useIsMobile();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Starts hidden so the box is never baked into the prerendered HTML and never
  // flashes on hydration. A client-only effect (below) reveals it after a short
  // delay on each fresh page load; dismissal lives in React state only.
  const [showAppBox, setShowAppBox] = useState(false);

  // Hero blur-up: the real <img> fades in over an inline blurred placeholder so
  // the loading state is the photo, never grey. The hero is preloaded and often
  // finishes before hydration attaches onLoad, so also check .complete on mount.
  const heroImgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (heroImgRef.current?.complete) setImageLoaded(true);
  }, []);

  // Booking-app nudge: revealed client-side after a delay that clears the
  // prerender snapshot + hydration, so it makes a deliberate entrance instead of
  // flashing in from the static HTML. Gated on the module flag so it fires once
  // per page load — a full refresh shows it again, but SPA route changes back to
  // the homepage do not re-pop it. position:fixed, so showing it never shifts
  // layout (CLS-neutral).
  useEffect(() => {
    if (appBoxScheduled) return;
    appBoxScheduled = true;
    const t = window.setTimeout(() => setShowAppBox(true), 2000);
    return () => window.clearTimeout(t);
  }, []);
  const heroBlurStyle = {
    backgroundImage: `url(${HOME_HERO_BLUR})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    filter: "blur(24px)",
    transform: "scale(1.1)",
  } as const;

  const facilityImages = [
    { src: "/facility/real-1.webp", alt: "Group of players socializing on courts" },
    { src: "/facility/real-3.webp", alt: "Players during a doubles match with Velto sign" },
    { src: "/facility/real-4.webp", alt: "Premium lounge area overlooking courts" },
    { src: "/facility/real-5.webp", alt: "Lounge with lockers, seating and dining table" },
    { src: "/facility/real-6.webp", alt: "Seating area with gallery wall and phone booths" },
    { src: "/facility/real-7.webp", alt: "Cozy lounge seating with sectional sofa" },
    { src: "/facility/real-8.webp", alt: "Wide facility view showing courts and lounge" },
  ];
  
  // Dismiss for the current page view only (in-memory) — a refresh brings it back.
  const handleCloseAppBox = () => {
    setShowAppBox(false);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      toast("Please enter a valid email address");
      return;
    }

    // Consent to be texted is meaningless without a number to text. And only a
    // ticked box saves a number: a number typed without consent is dropped.
    const smsPhone = smsOptIn ? toUsE164(phone) : null;
    if (smsOptIn && !smsPhone) {
      toast("Please add a valid US mobile number", {
        description: "We need it to send you texts, or you can untick the box."
      });
      return;
    }

    setSubmitting(true);

    try {
      // Supabase is only needed on submit — lazy-loaded to keep it out of the
      // homepage's critical bundle.
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase
        .from('emails')
        .insert([{ email }]);

      // An email we already have isn't a failure: someone already on the list
      // may be back just to opt in to texts, and that still needs saving.
      const alreadySignedUp =
        !!error && (error.code === '23505' || !!error.message?.includes('unique_email'));

      if (error && !alreadySignedUp) {
        console.error("Error saving email:", error);
        toast("Error saving email. Please try again.");
        setSubmitting(false);
        return;
      }

      if (!alreadySignedUp) {
        // Also send to ActiveCampaign (fire-and-forget)
        supabase.functions.invoke('activecampaign-signup', {
          body: { email },
        }).then(({ error: acError }) => {
          if (acError) console.error("ActiveCampaign sync error:", acError);
        });
      }

      if (smsPhone) {
        const { error: smsError } = await supabase
          .from('sms_consents')
          .insert([{
            phone: smsPhone,
            email,
            consent_text: SMS_CONSENT_TEXT,
            source: 'homepage_signup',
          }]);

        if (smsError) {
          console.error("Error saving SMS opt-in:", smsError);
          // Fields are left filled in: the email is already saved, so a
          // resubmit lands in the alreadySignedUp path and retries only this.
          toast("We couldn't save your mobile number", {
            description: "Your email is saved. Please try again to sign up for texts."
          });
          setSubmitting(false);
          return;
        }
      }

      setEmail("");
      setPhone("");
      setSmsOptIn(false);
      setSubmitting(false);

      if (alreadySignedUp && !smsPhone) {
        toast("We already have your email!", {
          description: "Be on the lookout for updates soon!"
        });
      } else if (alreadySignedUp) {
        toast("You're signed up for texts!", {
          description: "We already had your email, and we've added your mobile number."
        });
      } else {
        toast("Thank you for signing up! We'll keep you updated.", {
          description: smsPhone
            ? "You're signed up for texts too."
            : "Stay tuned for exclusive updates and events."
        });
      }
    } catch (error) {
      console.error("Error saving email:", error);
      toast("Error saving email. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <Helmet>
        <title>Velto Pickleball Club | Indoor Pickleball in Brooklyn, NYC</title>
        <meta name="description" content="Brooklyn's premier indoor pickleball courts in Red Hook, NYC. 4 courts from $80/hr. Open play, lessons, leagues & events. Daily 6AM–12AM." />
        <link rel="canonical" href="https://veltopb.com/" />
        <meta property="og:title" content="Velto Pickleball Club | Indoor Pickleball in Brooklyn, NYC" />
        <meta property="og:description" content="Brooklyn's premier indoor pickleball courts in Red Hook, NYC. 4 courts from $80/hr. Open play, lessons, leagues & events." />
        <meta property="og:url" content="https://veltopb.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://veltopb.com/og-default.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Velto Pickleball Club | Indoor Pickleball in Brooklyn, NYC" />
        <meta name="twitter:description" content="Brooklyn's premier indoor pickleball courts in Red Hook, NYC. 4 courts from $80/hr. Open play, lessons, leagues & events." />
        <meta name="twitter:image" content="https://veltopb.com/og-default.jpg" />
        {/* Page-level JSON-LD. Because Helmet marks these data-rh, the prerender
            strips index.html's two static twins on the homepage — so these must
            carry the FULL structured data (SportsActivityLocation + the rich
            LocalBusiness with amenityFeature/hasOfferCatalog), not a subset. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SportsActivityLocation",
          // Same @id as the LocalBusiness block below, so the two resolve to one
          // entity instead of reading as two businesses at the same address.
          // SportsActivityLocation is a subtype of LocalBusiness, so this is a
          // legitimate merge rather than a claim about two different things.
          "@id": "https://veltopb.com/#business",
          name: "Velto Pickleball Club",
          legalName: "O-O-2 PICKLEBALL USA LLC",
          description:
            "Brooklyn's premier indoor pickleball facility in Red Hook, NYC. 4 professional courts, open play, lessons, leagues & private events.",
          url: "https://veltopb.com/",
          image: "https://veltopb.com/og-default.jpg",
          address: {
            "@type": "PostalAddress",
            streetAddress: "160 Van Brunt Street",
            addressLocality: "Brooklyn",
            addressRegion: "NY",
            postalCode: "11231",
            addressCountry: "US",
          },
          geo: { "@type": "GeoCoordinates", latitude: 40.68311176663039, longitude: -74.00655090304011 },
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "06:00",
            closes: "00:00",
          },
          priceRange: "$$",
          sameAs: ["https://www.instagram.com/veltopb"],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://veltopb.com/#business",
          name: "Velto Pickleball Club",
          legalName: "O-O-2 PICKLEBALL USA LLC",
          description:
            "Brooklyn's premier indoor pickleball courts in Red Hook, NYC. 14,000 sq ft facility with 4 professional courts, pro shop, and lounge.",
          url: "https://veltopb.com/",
          image: "https://veltopb.com/og-default.jpg",
          email: "support@veltopb.com",
          telephone: "+1-516-968-5204",
          hasMap: "https://maps.google.com/?cid=14914183563513840067",
          priceRange: "$$",
          paymentAccepted: "Credit Card, Debit Card",
          currenciesAccepted: "USD",
          address: {
            "@type": "PostalAddress",
            streetAddress: "160 Van Brunt Street",
            addressLocality: "Brooklyn",
            addressRegion: "NY",
            postalCode: "11231",
            addressCountry: "US",
          },
          geo: { "@type": "GeoCoordinates", latitude: 40.68311176663039, longitude: -74.00655090304011 },
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "06:00",
            closes: "00:00",
          },
          amenityFeature: [
            { "@type": "LocationFeatureSpecification", name: "4 Indoor Pickleball Courts", value: true },
            { "@type": "LocationFeatureSpecification", name: "Premium Lounge Area", value: true },
            { "@type": "LocationFeatureSpecification", name: "Automated Pro Shop", value: true },
            { "@type": "LocationFeatureSpecification", name: "Private Phone Booths", value: true },
            { "@type": "LocationFeatureSpecification", name: "NYC Skyline Views", value: true },
            { "@type": "LocationFeatureSpecification", name: "Smart Fridges with Gourmet Meals", value: true },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Velto Pickleball Services",
            itemListElement: [
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Court Booking" }, description: "Reserve a pickleball court starting at $80/hr", priceCurrency: "USD", price: "80.00" },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Open Play Sessions" }, description: "Drop-in open play sessions for all skill levels" },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Private Lessons" }, description: "One-on-one instruction with certified pickleball coaches" },
              { "@type": "Offer", itemOffered: { "@type": "Service", name: "Private Events" }, description: "Corporate events, birthday parties, and team building at Velto Pickleball Club" },
              // The purchasable packs, from the same source the visible
              // Packages section renders, so price and contents cannot drift.
              ...packs.map((pack) => ({
                "@type": "Offer",
                itemOffered: { "@type": "Product", name: pack.name },
                description: [...pack.includes, pack.expiry].join(", "),
                priceCurrency: "USD",
                price: pack.priceValue,
                url: PACKS_URL,
              })),
            ],
          },
          areaServed: [
            { "@type": "City", name: "Brooklyn" },
            { "@type": "City", name: "New York City" },
          ],
          sameAs: ["https://www.instagram.com/veltopb"],
        })}</script>
        {/* Site-level Organization entity with sameAs (social profiles + press
            page) so Google can associate third-party coverage with the brand. */}
        {/* TODO-JAWAD[instagram-handle]: The site references two Instagram handles — instagram.com/veltopb (in the LocalBusiness/SportsActivityLocation schema above) and instagram.com/veltopickleball (in the visible footer/contact links, used in this Organization block). Confirm the correct handle and unify. — Option A: veltopickleball | Option B: veltopb | ANSWER: ____ */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Velto Pickleball Club",
          legalName: "O-O-2 PICKLEBALL USA LLC",
          url: "https://veltopb.com/",
          logo: "https://veltopb.com/Velto_04_Crest_Forest.png",
          sameAs: [
            "https://www.instagram.com/veltopickleball/",
            "https://www.facebook.com/profile.php?id=61579844015071",
            "https://www.tiktok.com/@veltopickleball",
            "https://veltopb.com/press",
          ],
        })}</script>
      </Helmet>
      <Header />
      {/* SEO H1 — visually hidden */}
      <h1 className="sr-only">Velto Pickleball Club — Indoor Pickleball Courts in Brooklyn, NYC</h1>

      {/* Hero Section */}
      <section
        className="h-screen w-full flex flex-col items-center justify-center relative overflow-hidden"
        style={{ paddingTop: "calc(5rem + var(--banner-h, 0px))" }}
      >

      {/* Blur-up. Two inline low-entropy placeholders (never LCP-eligible):
          - the BEHIND layer is always on, so the loading state is the photo and
            never grey even before JS runs or on a slow/hard-refresh load;
          - the real <img> keeps opacity:1 so it stays the LCP element (fading an
            LCP image demotes it — verified: LCP moved to the headline text);
          - the ON-TOP layer fades out once the photo loads for a smooth reveal
            (instant, no fade, under prefers-reduced-motion). */}
      <div aria-hidden="true" className="absolute inset-0 z-0" style={heroBlurStyle} />
      <picture>
        <source
          type="image/avif"
          srcSet="/hero-facility-640.avif 640w, /hero-facility-960.avif 960w, /hero-facility-1280.avif 1280w, /hero-facility-1600.avif 1600w"
          sizes="100vw"
        />
        <img
          ref={heroImgRef}
          src="/hero-facility-960.webp"
          srcSet="/hero-facility-640.webp 640w, /hero-facility-960.webp 960w, /hero-facility-1280.webp 1280w, /hero-facility-1600.webp 1600w"
          sizes="100vw"
          alt="Velto Pickleball Club facility interior in Red Hook, Brooklyn"
          width={1536}
          height={1024}
          {...({ fetchpriority: "high" } as any)}
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
      </picture>
      <div
        aria-hidden="true"
        className={`absolute inset-0 z-0 pointer-events-none transition-opacity duration-700 motion-reduce:transition-none ${imageLoaded ? "opacity-0" : "opacity-100"}`}
        style={heroBlurStyle}
      />
      <div className="absolute inset-0 bg-black/50 z-0"></div>

      <div className={`relative z-10 transition-opacity duration-1000 ${loading ? 'opacity-0' : 'opacity-100'} w-full flex justify-center`}>
        <div className="p-4 md:p-12 rounded-lg flex flex-col items-center justify-center w-full md:w-auto px-4">
          <p className="text-3xl md:text-6xl uppercase tracking-widest font-light mb-3 md:mb-4 coming-soon-text select-none text-center font-sweet-bold leading-tight" style={{ color: 'hsl(var(--velto-beige))' }}>
            What NYC Deserves
            <br />
            <span className="mt-2 inline-block">Now Open</span>
          </p>

          <p className="text-sm md:text-lg italic text-white/80 mb-8 md:mb-10 text-center px-2 font-sweet">
            Redefining the Pickleball Experience
          </p>

          <div className="w-full max-w-2xl mx-auto flex flex-col items-center">
            <div className="flex flex-col sm:flex-row gap-4 w-full items-stretch justify-center">
              <a
                href={withAttribution("https://velto.podplay.app/book")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackBooking("hero_primary", "https://velto.podplay.app/book")}
                className="flex-1 inline-flex flex-col items-center justify-center px-8 py-4 md:py-5 min-h-[56px] md:min-h-[64px] uppercase tracking-wider font-sweet-bold text-base md:text-lg transition-all hover:-translate-y-0.5"
                style={{ backgroundColor: 'hsl(var(--velto-copper))', color: 'hsl(var(--velto-beige))' }}
              >
                <span>Book a Court</span>
                <span className="mt-1 normal-case tracking-normal italic font-sweet text-[10px] md:text-xs opacity-90">(paddles & balls included)</span>
              </a>
              <a
                href={withAttribution("https://velto.podplay.app/packages/nyc-brooklyn")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackBooking("hero_packages", "https://velto.podplay.app/packages/nyc-brooklyn")}
                className="flex-1 inline-flex items-center justify-center px-8 py-4 md:py-5 min-h-[56px] md:min-h-[64px] uppercase tracking-wider font-sweet-bold text-base md:text-lg border-2 bg-transparent hover:bg-white/10 transition-all hover:-translate-y-0.5"
                style={{ borderColor: 'hsl(var(--velto-beige))', color: 'hsl(var(--velto-beige))' }}
              >
                Buy a Package
              </a>
            </div>
            {/* Secondary to the two buttons above, not a third peer: a 1px
                border against their 2px, no fill, and a shorter box. Kept as
                block/text-center rather than flex so the {" "} between the
                two runs still renders as a real word space. */}
            <Link
              to="/events"
              className="mt-4 w-full block text-center px-6 py-3.5 border font-sweet-bold text-sm md:text-base tracking-wide transition-all hover:-translate-y-0.5 hover:bg-white/10"
              style={{ borderColor: 'hsl(var(--velto-beige) / 0.45)', color: 'hsl(var(--velto-beige))' }}
            >
              Planning a group event or party? Host it at{" "}
              <span className="whitespace-nowrap">Velto →</span>
            </Link>
          </div>
        </div>
      </div>

      </section>

      {/* Welcome Section */}
      <section id="welcome" className="py-16 md:py-24" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="space-y-6" style={{ color: 'hsl(var(--velto-forest))' }}>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight font-instrument">
                Welcome to Velto Pickleball Club — Brooklyn, NYC
              </h2>
              <ul className="space-y-2 list-disc list-inside">
                <li className="text-lg md:text-xl font-sweet font-bold">
                  4 Laykold Cushion plus Pickleball Courts
                </li>
                <li className="text-lg md:text-xl font-sweet font-bold">
                  Premium lounge & work spaces
                </li>
                <li className="text-lg md:text-xl font-sweet font-bold">
                  The best views of NYC
                </li>
                <li className="text-lg md:text-xl font-sweet font-bold">
                  Only 10 minutes from FiDi
                </li>
              </ul>
              <div className="w-24 h-1 rounded" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}></div>
              <div className="text-base md:text-lg leading-relaxed space-y-4 font-sweet">
                <p>
                  Velto is New York City's first fully automated, premium pickleball club, in the heart of Red Hook, Brooklyn.
                </p>
                <p>
                  Play competitive matches, join open play, or take lessons in a premium setting designed for every skill level. Off the court, relax in our lounges or get work done in modern coworking spaces that keep you connected.
                </p>
              </div>
            </div>
            
            {/* Right Side - Image Carousel */}
            <div className="relative flex justify-center">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-3/4 md:w-4/5 lg:w-3/4 max-w-md"
              >
                <CarouselContent>
                  {facilityImages.map((image, index) => (
                    <CarouselItem key={index}>
                      <img
                        src={image.src}
                        srcSet={facilitySrcSet(image.src)}
                        sizes="(min-width: 1024px) 448px, 270px"
                        alt={image.alt}
                        width={1536}
                        height={1024}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto rounded-lg shadow-2xl object-cover"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious 
                  className="left-2 bg-white/90 hover:bg-white border-velto-forest text-velto-forest"
                />
                <CarouselNext 
                  className="right-2 bg-white/90 hover:bg-white border-velto-forest text-velto-forest"
                />
              </Carousel>
            </div>
          </div>

          {/* Community Chat + Email Signup */}
          {/* The chat link is deliberately NOT book_court_click: joining the
              community chat is not booking intent, and folding it in would
              inflate the very conversion count this work exists to make
              trustworthy. */}
          <div className="mt-12 md:mt-16 flex flex-col items-center">
            <a
              href={withAttribution("https://velto.podplay.app/community/chat")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent("community_chat_click", {
                  page: "/",
                  location: "community_chat",
                  destination: "https://velto.podplay.app/community/chat",
                })
              }
              className="text-sm md:text-base underline font-sweet transition-colors hover:opacity-80"
              style={{ color: 'hsl(var(--velto-copper))' }}
            >
              Join Our Community Chat
            </a>

            <form onSubmit={handleEmailSubmit} className="mt-6 w-full max-w-md flex flex-col sm:flex-row gap-2 items-stretch">
              <Input
                type="email"
                placeholder="Email address"
                aria-label="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/80 border-velto-forest/40 w-full text-sm md:text-base h-11"
                style={{ color: 'hsl(var(--velto-forest))' }}
                required
                disabled={submitting}
              />
              <Button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto text-sm md:text-base h-11 whitespace-nowrap font-sweet-bold uppercase tracking-wider hover:opacity-90"
                style={{ backgroundColor: 'hsl(var(--velto-copper))', color: 'hsl(var(--velto-beige))' }}
              >
                Keep Me Updated
              </Button>
            </form>
            <p className="text-xs text-center mt-2 font-sweet" style={{ color: 'hsl(var(--velto-forest) / 0.75)' }}>
              Stay in the loop on offerings and events
            </p>

            {/* SMS opt-in. Unchecked by default and never a condition
                of anything. The disclosure text below is required near-verbatim
                by carriers (brand name, registered entity, "not a condition of
                purchase", frequency, rates, STOP and HELP) — restyle freely,
                don't cut words out of it. */}
            <div
              className="mt-6 w-full max-w-md rounded-xl border px-5 py-4"
              style={{
                borderColor: 'hsl(var(--velto-copper) / 0.3)',
                backgroundColor: 'hsl(0 0% 100% / 0.45)',
              }}
            >
              <Input
                type="tel"
                placeholder="Mobile number (optional)"
                aria-label="Mobile number, optional"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="bg-white/80 border-velto-forest/40 w-full text-sm md:text-base h-11"
                style={{ color: 'hsl(var(--velto-forest))' }}
                disabled={submitting}
              />

              <label
                htmlFor="smsOptIn"
                className="mt-4 flex items-center justify-center gap-3 cursor-pointer font-sweet"
              >
                <input
                  id="smsOptIn"
                  name="smsOptIn"
                  type="checkbox"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  disabled={submitting}
                  className="h-4 w-4 shrink-0 cursor-pointer"
                  style={{ accentColor: 'hsl(var(--velto-copper))' }}
                />
                <span className="text-sm" style={{ color: 'hsl(var(--velto-forest))' }}>
                  {SMS_OPT_IN_LABEL}
                </span>
              </label>

              <p
                className="text-[10px] leading-[1.6] text-center mt-4 font-sweet"
                style={{ color: 'hsl(var(--velto-forest) / 0.6)' }}
              >
                {SMS_DISCLOSURE}{" "}
                See our{" "}
                <Link to="/terms-of-use" className="underline hover:opacity-80">
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy-policy" className="underline hover:opacity-80">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Play Pickleball Section */}
      <section id="programs" className="py-16 md:py-24" style={{ backgroundColor: 'hsl(var(--velto-forest))' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 font-instrument tracking-[0.15em]" style={{ color: 'hsl(var(--velto-beige))' }}>
              WAYS TO PLAY PICKLEBALL
            </h2>
            <div className="flex justify-center">
              <Link to="/levels" className="group relative inline-block">
                {/* Outer glow */}
                <div
                  className="absolute -inset-2 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ backgroundColor: 'hsl(var(--velto-copper) / 0.25)' }}
                />
                <button
                  className="relative overflow-hidden inline-flex items-center gap-4 px-10 md:px-12 py-5 md:py-6 font-black uppercase tracking-[0.25em] text-xs md:text-sm border shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] transition-all duration-500 group-hover:-translate-y-1 group-active:scale-95 font-sweet-bold"
                  style={{
                    backgroundColor: 'hsl(var(--velto-copper))',
                    color: 'hsl(var(--velto-beige))',
                    borderColor: '#b5894b',
                  }}
                >
                  {/* Shine sweep */}
                  <span className="pointer-events-none absolute inset-0 w-1/2 h-full bg-white/10 -skew-x-[45deg] -translate-x-[150%] group-hover:translate-x-[250%] transition-transform duration-1000 ease-in-out" />
                  <span>Find Events For Your Level</span>
                  <svg
                    className="w-5 h-5 transition-transform duration-500 group-hover:translate-x-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </Link>
            </div>
            <div className="mt-8 max-w-4xl mx-auto">
              <p className="font-sweet-bold text-sm uppercase tracking-[0.15em] mb-3" style={{ color: 'hsl(var(--velto-beige))' }}>
                Our schedule is color coded
              </p>
              <ColorKey variant="compact" onDark />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8">
            {/* Card 1: Book a Court */}
            <div className="rounded-lg shadow-lg p-4 relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full min-h-[200px]" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                1
              </div>
              <div className="mt-8 flex flex-col h-full">
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'hsl(var(--velto-forest))' }}>
                    BOOK A COURT <span className="italic font-normal text-base" style={{ color: 'hsl(var(--velto-copper))' }}>from $80</span>
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    Reserve your court with our easy online booking system (paddles & balls included).
                  </p>
                </div>
                <div className="mt-auto pt-2">
                  <a
                    href={withAttribution("https://velto.podplay.app/book")}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackBooking("services_book_court", "https://velto.podplay.app/book")}
                  >
                    <Button 
                      className="w-full font-semibold text-white hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'hsl(var(--velto-copper))' }}
                    >
                      BOOK
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Card 2: Open Play */}
            <div className="rounded-lg shadow-lg p-4 relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full min-h-[200px]" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                2
              </div>
              <div className="mt-8 flex flex-col h-full">
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'hsl(var(--velto-forest))' }}>
                    OPEN PLAY
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    {OPEN_PLAY_SUMMARY}
                  </p>
                </div>
                <div className="mt-auto pt-2">
                  <a
                    href={withAttribution("https://velto.podplay.app/community/events")}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackBooking("services_open_play", "https://velto.podplay.app/community/events")}
                  >
                    <Button 
                      className="w-full font-semibold text-white hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'hsl(var(--velto-copper))' }}
                    >
                      BOOK
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Card 3: Lessons & Clinics */}
            <div className="rounded-lg shadow-lg p-4 relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full min-h-[200px]" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                3
              </div>
              <div className="mt-8 flex flex-col h-full">
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'hsl(var(--velto-forest))' }}>
                    LESSONS & CLINICS <span className="italic font-normal text-base" style={{ color: 'hsl(var(--velto-copper))' }}>from $25</span>
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    Improve your game with our professionally designed curriculum.
                  </p>
                </div>
                <div className="mt-auto pt-2">
                  <a
                    href={withAttribution("https://velto.podplay.app/community/events?tag=Clinics")}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackBooking("services_lessons_clinics", "https://velto.podplay.app/community/events?tag=Clinics")}
                  >
                    <Button 
                      className="w-full font-semibold text-white hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'hsl(var(--velto-copper))' }}
                    >
                      BOOK
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Card 4: Coaching */}
            <div className="rounded-lg shadow-lg p-4 relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full min-h-[200px]" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                4
              </div>
              <div className="mt-8 flex flex-col h-full">
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'hsl(var(--velto-forest))' }}>
                    COACHING
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    Select from our expert coaches for personalized instruction.
                  </p>
                </div>
                <div className="mt-auto pt-2">
                  <a
                    href={withAttribution("https://velto.podplay.app/community/events")}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackBooking("services_coaching", "https://velto.podplay.app/community/events")}
                  >
                    <Button 
                      className="w-full font-semibold text-white hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'hsl(var(--velto-copper))' }}
                    >
                      BOOK
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Card 5: Private Events */}
            <div className="rounded-lg shadow-lg p-4 relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full min-h-[200px]" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                5
              </div>
              <div className="mt-8 flex flex-col h-full">
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'hsl(var(--velto-forest))' }}>
                    PRIVATE EVENTS
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    Host your private or corporate events at Velto.
                  </p>
                </div>
                <div className="mt-auto pt-2">
                  <Link
                    to="/events"
                    className="w-full inline-block text-center font-semibold text-white hover:opacity-90 transition-opacity py-2 px-4 rounded-md"
                    style={{ backgroundColor: 'hsl(var(--velto-copper))' }}
                  >
                    View private event details
                  </Link>
                </div>
              </div>
            </div>

            {/* Card 6: Leagues */}
            <div className="rounded-lg shadow-lg p-4 relative hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full min-h-[200px]" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
              <div className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                6
              </div>
              <div className="mt-8 flex flex-col h-full">
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: 'hsl(var(--velto-forest))' }}>
                    LEAGUES
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                    Join competitive organized play with structured competitions.
                  </p>
                </div>
                <div className="mt-auto pt-2">
                  <a
                    href={withAttribution("https://velto.podplay.app/community/events?tag=Leagues")}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackBooking("services_leagues", "https://velto.podplay.app/community/events?tag=Leagues")}
                  >
                    <Button 
                      className="w-full font-semibold text-white hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: 'hsl(var(--velto-copper))' }}
                    >
                      BOOK
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages Pricing Section */}
      <section className="py-16 md:py-24 pb-8 md:pb-12" style={{ backgroundColor: 'hsl(var(--velto-forest))' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <img
              src={veltoLogoBeige}
              alt="Velto Pickleball Club logo"
              className="h-28 md:h-36 w-auto mx-auto mb-6"
              loading="lazy"
              width={400}
              height={400}
            />
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 font-instrument" style={{ color: 'hsl(var(--velto-beige))' }}>
              Packages
            </h2>
            <div className="w-24 h-1 rounded mx-auto" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}></div>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-stretch">
            {packs.map((pack) => (
              <div key={pack.id} className="rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
                {/* Flex column so the cards — which have different numbers of
                    bullets — keep their buttons on a shared baseline. The list
                    sits in a flex-1 box and is centred inside it, so the slack
                    splits above and below rather than pooling in one gap. */}
                <div className="p-8 flex flex-col h-full">
                  {/* Title — the card's visual anchor now that the button no
                      longer names the pack. One step up from the price beside
                      it (text-3xl), same font and colour as before. */}
                  <h3 className="text-4xl md:text-5xl font-bold mb-4 font-instrument" style={{ color: 'hsl(var(--velto-forest))' }}>
                    {pack.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-3xl font-bold" style={{ color: 'hsl(var(--velto-forest))' }}>{pack.price}</span>
                    <span className="text-lg ml-2" style={{ color: 'hsl(var(--velto-forest))' }}>{pack.cadence}{pack.value && ` · ${pack.value}`}</span>
                  </div>

                  {/* Specifics */}
                  <div className="mb-8 flex-1 flex flex-col justify-center">
                    <h4 className="font-sweet-bold font-bold text-base md:text-lg uppercase tracking-wider mb-4" style={{ color: 'hsl(var(--velto-forest))' }}>Specifics</h4>
                    <div className="space-y-4">
                      {[...pack.includes, pack.expiry].map((item) => (
                        <div key={item} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0" style={{ borderColor: 'hsl(var(--velto-forest))' }}>
                            <svg className="w-3 h-3" style={{ color: 'hsl(var(--velto-forest))' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <span className="text-lg" style={{ color: 'hsl(var(--velto-forest))' }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    size="lg"
                    asChild
                    // h-auto + whitespace-normal: Button's base sets
                    // whitespace-nowrap and size:default pins h-10, so the long
                    // pack names overflowed the pill at 375px instead of wrapping.
                    className="w-full py-6 text-xl font-semibold rounded-full h-auto whitespace-normal text-center leading-snug"
                    style={{ backgroundColor: 'hsl(var(--velto-copper))', color: 'hsl(var(--velto-beige))' }}
                  >
                    <a
                      href={withAttribution(PACKS_URL)}
                      target="_blank"
                      rel="noopener noreferrer"
                      // Both buttons read "Grab This Pack", so the pack name
                      // moves to the accessible name.
                      aria-label={packCtaAriaLabel(pack)}
                      // link_text carries the pack name so the cards stay
                      // distinguishable — they all share one destination URL.
                      onClick={() =>
                        trackEvent("book_court_click", {
                          page: "/",
                          location: "packs_card",
                          destination: PACKS_URL,
                          link_text: pack.name,
                        })
                      }
                    >
                      {PACK_CTA_LABEL}
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section className="pt-8 md:pt-12 pb-16 md:pb-24" style={{ backgroundColor: 'hsl(var(--velto-forest))' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-md mx-auto">
            <div className="rounded-xl shadow-lg p-8" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
              <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center font-instrument" style={{ color: 'hsl(var(--velto-forest))' }}>
                OPENING HOURS
              </h3>
              <div className="text-center mb-6">
                <p className="font-sweet-bold font-bold text-base md:text-lg uppercase tracking-wider mb-4" style={{ color: 'hsl(var(--velto-forest))' }}>
                  Red Hook, Brooklyn, NYC
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span style={{ color: 'hsl(var(--velto-forest))' }}>Monday - Sunday</span>
                    <span className="font-bold" style={{ color: 'hsl(var(--velto-forest))' }}>6am - 12am</span>
                  </div>
                </div>
              </div>
              
              {/* Social Media Icons */}
              <div className="flex justify-center space-x-4 pt-6 border-t" style={{ borderColor: 'hsl(var(--velto-forest) / 0.2)' }}>
                <a href="https://www.facebook.com/profile.php?id=61579844015071#" target="_blank" rel="noopener noreferrer" aria-label="Velto on Facebook" className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity" style={{ backgroundColor: 'hsl(var(--velto-forest))' }}>
                  <svg className="w-5 h-5" style={{ fill: 'hsl(var(--velto-beige))' }} viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/veltopickleball/" target="_blank" rel="noopener noreferrer" aria-label="Velto on Instagram" className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity" style={{ backgroundColor: 'hsl(var(--velto-forest))' }}>
                  <svg className="w-5 h-5" style={{ fill: 'hsl(var(--velto-beige))' }} viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.tiktok.com/@veltopickleball" target="_blank" rel="noopener noreferrer" aria-label="Velto on TikTok" className="w-10 h-10 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity" style={{ backgroundColor: 'hsl(var(--velto-forest))' }}>
                  <svg className="w-5 h-5" style={{ fill: 'hsl(var(--velto-beige))' }} viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Frictionless Booking Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 font-instrument tracking-[0.15em]" style={{ color: 'hsl(var(--velto-forest))' }}>
              FRICTIONLESS BOOKING
            </h2>
            <p className="text-lg md:text-xl" style={{ color: 'hsl(var(--velto-forest))' }}>
              BOOK ONLINE OR VIA OUR CLUB APPLICATION
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="space-y-8">
              {/* App Store Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a
                  href="https://apps.apple.com/us/app/velto-pickleball-club/id6751204565"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black text-white px-6 py-3 rounded-lg flex items-center gap-3 cursor-pointer hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div>
                    <div className="text-xs">Download on the</div>
                    <div className="text-sm font-semibold">App Store</div>
                  </div>
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.veltopickleball.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-black text-white px-6 py-3 rounded-lg flex items-center gap-3 cursor-pointer hover:bg-gray-800 transition-colors"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div>
                    <div className="text-xs">Get it on</div>
                    <div className="text-sm font-semibold">Google Play</div>
                  </div>
                </a>
              </div>

              {/* Three Step Process */}
              <div className="space-y-6">
                <div className="flex items-center gap-4 group cursor-pointer" onClick={() => window.location.href = '/book'}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                    1
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold group-hover:underline" style={{ color: 'hsl(var(--velto-forest))' }}>
                      CHOOSE THE WAY YOU WANT TO PLAY
                    </h3>
                    <p className="text-sm opacity-75" style={{ color: 'hsl(var(--velto-forest))' }}>
                      Select from court bookings, open plays, or clinics
                    </p>
                  </div>
                  <svg className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: 'hsl(var(--velto-copper))' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>

                <div className="flex items-center gap-4 group cursor-pointer" onClick={() => window.location.href = '/book'}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                    2
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold group-hover:underline" style={{ color: 'hsl(var(--velto-forest))' }}>
                      CONFIGURE YOUR BOOKING
                    </h3>
                    <p className="text-sm opacity-75" style={{ color: 'hsl(var(--velto-forest))' }}>
                      Choose your preferred time, court, and duration
                    </p>
                  </div>
                  <svg className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: 'hsl(var(--velto-copper))' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>

                <div className="flex items-center gap-4 group cursor-pointer" onClick={() => window.location.href = '/contact'}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}>
                    3
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold group-hover:underline" style={{ color: 'hsl(var(--velto-forest))' }}>
                      REGISTER AN ACCOUNT
                    </h3>
                    <p className="text-sm opacity-75" style={{ color: 'hsl(var(--velto-forest))' }}>
                      Create your profile and complete your booking
                    </p>
                  </div>
                  <svg className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: 'hsl(var(--velto-copper))' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Right Side - Phone Image */}
            <div className="flex justify-center">
              <img
                src="/facility/booking-section.webp"
                srcSet={facilitySrcSet("/facility/booking-section.webp")}
                sizes="(min-width: 768px) 448px, calc(100vw - 2rem)"
                alt="Velto Pickleball Club facility with courts and lounge"
                width={1536}
                height={1024}
                loading="lazy"
                decoding="async"
                className="w-full max-w-md mx-auto rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact & Location Section */}
      <section id="contact" className="py-16 md:py-24" style={{ backgroundColor: 'hsl(var(--velto-forest))' }}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Contact Info */}
            <div className="space-y-6" style={{ color: 'hsl(var(--velto-copper))' }}>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight font-instrument">
                Now Open
              </h2>
              <div className="w-24 h-1 rounded" style={{ backgroundColor: 'hsl(var(--velto-copper))' }}></div>
              
              <div className="space-y-4 text-base md:text-lg">
                <div>
                  <h3 className="font-sweet-bold font-bold text-base md:text-lg uppercase tracking-wider mb-2">Contact Us</h3>
                  <a 
                    href="mailto:support@veltopb.com" 
                    className="hover:text-white/80 transition-colors"
                  >
                    support@veltopb.com
                  </a>
                  <div className="mt-1">
                    <a
                      href="tel:+15169685204"
                      className="hover:text-white/80 transition-colors"
                      onClick={() => {
                        trackEvent("phone_click", {
                          page_path: typeof window !== "undefined" ? window.location.pathname : "/",
                          link_text: "(516) 968-5204",
                        });
                      }}
                    >
                      (516) 968-5204
                    </a>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-sweet-bold font-bold text-base md:text-lg uppercase tracking-wider mb-2">Visit Us</h3>
                  <address className="not-italic leading-relaxed">
                    160 Van Brunt St<br />
                    Brooklyn, NY 11231
                  </address>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=160+Van+Brunt+St+Brooklyn+NY+11231"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 underline hover:text-white/80 transition-colors text-base"
                  >
                    Get directions →
                  </a>
                </div>

                <div>
                  <h3 className="font-sweet-bold font-bold text-base md:text-lg uppercase tracking-wider mb-2">Hours</h3>
                  <p className="text-base md:text-lg leading-relaxed">
                    Open daily 6:00 AM – 12:00 AM
                  </p>
                </div>

                <div>
                  <h3 className="font-sweet-bold font-bold text-base md:text-lg uppercase tracking-wider mb-2">Getting Here</h3>
                  <p className="text-base md:text-lg leading-relaxed">
                    Subway: F or G to Smith–9th St, then B61 bus.<br />
                    Ferry: NYC Ferry South Brooklyn line to Red Hook / Atlantic Basin.<br />
                    Parking: free street parking on Van Brunt and surrounding blocks.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Right Side - Google Map */}
            <div className="relative">
              <div className="w-full h-80 md:h-96 rounded-lg overflow-hidden shadow-2xl">
                <iframe
                  src={MAP_EMBED_URL}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Velto Pickleball Club Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Download Box */}
      {showAppBox && (
        <div className="fixed bottom-6 right-6 z-[9999] hidden md:block pointer-events-auto">
          <div className="bg-[#8d632c] text-white p-6 rounded-xl shadow-2xl max-w-xs relative">
            <button
              onClick={handleCloseAppBox}
              className="absolute top-2 right-2 text-white hover:text-white/70 transition-colors p-1"
              aria-label="Close app download box"
            >
              <X size={16} />
            </button>
            <div className="text-center space-y-4">
              <h3 className="text-sm font-bold uppercase tracking-wide leading-tight">
                STREAMLINE YOUR BOOKING EXPERIENCE
              </h3>
              <p className="text-xs opacity-90 leading-relaxed">
                DOWNLOAD OUR BOOKING APP
              </p>
              
              <div className="flex flex-col space-y-3 pt-2">
              {/* Apple App Store Button */}
              <a 
                href="https://apps.apple.com/us/app/velto-pickleball-club/id6751204565" 
                className="flex items-center justify-center bg-black rounded-lg px-4 py-2 hover:bg-gray-800 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs opacity-75">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </a>
              
              {/* Google Play Store Button */}
              <a 
                href="https://play.google.com/store/apps/details?id=com.veltopickleball.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center bg-black rounded-lg px-4 py-2 hover:bg-gray-800 transition-colors"
              >
                <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs opacity-75">Get it on</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile App Download Box */}
      {showAppBox && (
        <div className="fixed bottom-20 right-4 z-[9999] md:hidden pointer-events-auto">
          <div className="bg-[#8d632c] text-white p-4 rounded-lg shadow-xl max-w-[280px] relative">
            <button
              onClick={handleCloseAppBox}
              className="absolute top-2 right-2 text-white hover:text-white/70 transition-colors p-1"
              aria-label="Close app download box"
            >
              <X size={14} />
            </button>
            <div className="text-center space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wide leading-tight">
                DOWNLOAD OUR APP
              </h3>
              
              <div className="flex space-x-2">
              {/* Apple App Store Button - Compact */}
              <a 
                href="https://apps.apple.com/us/app/velto-pickleball-club/id6751204565" 
                className="flex-1 flex items-center justify-center bg-black rounded-md px-2 py-2 hover:bg-gray-800 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs font-semibold">App Store</div>
                </div>
              </a>
              
              {/* Google Play Store Button - Compact */}
              <a 
                href="https://play.google.com/store/apps/details?id=com.veltopickleball.app"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center bg-black rounded-md px-2 py-2 hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs font-semibold">Google Play</div>
                </div>
              </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Explore — contextual in-content internal links (keyword anchors) */}
      <section className="py-16 md:py-24" style={{ backgroundColor: 'hsl(var(--velto-beige))' }}>
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-instrument" style={{ color: 'hsl(var(--velto-forest))' }}>
            Explore Pickleball at Velto
          </h2>
          <div className="space-y-5 text-base md:text-lg leading-relaxed font-sweet" style={{ color: 'hsl(var(--velto-forest))' }}>
            <p>
              Reserve a court by the hour with{" "}
              <Link to="/pickleball-court-rental-brooklyn" className="underline hover:text-[hsl(var(--velto-copper))] transition-colors">
                pickleball court rental in Brooklyn
              </Link>{" "}
              — no membership required — on our four{" "}
              <Link to="/indoor-pickleball-courts-brooklyn" className="underline hover:text-[hsl(var(--velto-copper))] transition-colors">
                indoor pickleball courts in Brooklyn
              </Link>
              , climate-controlled and playable year-round.
            </p>
            <p>
              New to the game or sharpening your third-shot drop? Book{" "}
              <Link to="/pickleball-lessons-brooklyn" className="underline hover:text-[hsl(var(--velto-copper))] transition-colors">
                pickleball lessons in Brooklyn
              </Link>{" "}
              with certified coaches, or drop into skill-leveled{" "}
              <Link to="/pickleball-open-play-brooklyn" className="underline hover:text-[hsl(var(--velto-copper))] transition-colors">
                open play sessions
              </Link>{" "}
              to meet the Red Hook pickleball community.
            </p>
            <p>
              Planning something bigger? We host{" "}
              <Link to="/corporate-pickleball-events-nyc" className="underline hover:text-[hsl(var(--velto-copper))] transition-colors">
                corporate pickleball events in NYC
              </Link>{" "}
              — team offsites, client experiences, and mixers — with a full facility buyout.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
